// レイヤー描画状態ストア（UI 状態の単一の真実）。
//
// 役割: 地図に表示する各レイヤーの描画オブジェクト（GeoJSON・色・表示可否・bounds・来歴）を
//       メモリ保持し、変更を購読者（React）へ通知する。undo/redo の履歴もここで持つ。
// 関係: React からは useSyncExternalStore で購読する（subscribe / getSnapshot）。
//       DuckDB 本体の操作（テーブル DROP や再生成）は行わず、undo/redo では「何をすべきか」を
//       表す副作用記述子を返し、実際の DuckDB 操作は呼び出し側（App）が担う。これによりストアは
//       ブラウザ非依存で単体テスト可能（e-Stat のストア注入/購読パターンに倣う）。
//
// レイヤー描画オブジェクトの形:
//   { layerId, displayName, featureCollection, color, visible, featureCount,
//     geomType, bounds, originPrompt, originSql, sourceLayers, createdAt }
//
// レイヤーソースの正本は DuckDB の layer_registry（plan.md §3.2）。本ストアはその描画用ミラー。

// レイヤーごとに循環割り当てする配色パレット。
const PALETTE = [
  [255, 140, 0],
  [0, 122, 255],
  [52, 199, 89],
  [175, 82, 222],
  [255, 45, 85],
  [255, 204, 0],
]

export class LayerStore {
  #layers = [] // 不変配列（更新のたび新しい参照に差し替え）
  #listeners = new Set()
  #undo = [] // 逆操作の履歴: { kind: 'add' | 'remove', layer }
  #redo = []

  // 次に割り当てるパレット色を返す。
  nextColor() {
    return PALETTE[this.#layers.length % PALETTE.length]
  }

  // 購読登録。返り値は解除関数（useSyncExternalStore 用）。
  subscribe = (listener) => {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  // 現在のレイヤー配列（同一参照を返すことで useSyncExternalStore の再描画を最小化）。
  getSnapshot = () => this.#layers

  #notify() {
    for (const listener of this.#listeners) listener()
  }

  #commit(nextLayers) {
    this.#layers = nextLayers
    this.#notify()
  }

  // レイヤーを追加する（履歴に積む。undo で drop される）。
  add(layer) {
    this.#commit([...this.#layers, layer])
    this.#undo.push({ kind: 'add', layer })
    this.#redo = []
  }

  // レイヤーを削除する（履歴に積む。undo で復元される）。戻り値は削除した描画オブジェクト。
  remove(layerId) {
    const layer = this.#layers.find((l) => l.layerId === layerId)
    if (!layer) return null
    this.#commit(this.#layers.filter((l) => l.layerId !== layerId))
    this.#undo.push({ kind: 'remove', layer })
    this.#redo = []
    return layer
  }

  // 部分更新（表示切替・色変更など。履歴には積まない）。
  update(layerId, patch) {
    this.#commit(
      this.#layers.map((l) => (l.layerId === layerId ? { ...l, ...patch } : l)),
    )
  }

  toggle(layerId) {
    const layer = this.#layers.find((l) => l.layerId === layerId)
    if (layer) this.update(layerId, { visible: !layer.visible })
  }

  get(layerId) {
    return this.#layers.find((l) => l.layerId === layerId) ?? null
  }

  clear() {
    this.#undo = []
    this.#redo = []
    this.#commit([])
  }

  canUndo() {
    return this.#undo.length > 0
  }

  canRedo() {
    return this.#redo.length > 0
  }

  // 直近の操作を取り消す。
  // 戻り値: 呼び出し側が実行すべき DuckDB 副作用の記述子（plan.md §6.3）。
  //   { effect: 'drop', layer }     … テーブルとレジストリを削除すべき
  //   { effect: 'recreate', layer } … origin_sql などからテーブルを再生成すべき
  //   null                          … 取り消す操作なし
  undo() {
    const entry = this.#undo.pop()
    if (!entry) return null
    if (entry.kind === 'add') {
      // add の取り消し → レイヤーを画面から消し、DuckDB からも drop する。
      this.#commit(this.#layers.filter((l) => l.layerId !== entry.layer.layerId))
      this.#redo.push(entry)
      return { effect: 'drop', layer: entry.layer }
    }
    // remove の取り消し → レイヤーを復元し、DuckDB のテーブルを再生成する。
    this.#commit([...this.#layers, entry.layer])
    this.#redo.push(entry)
    return { effect: 'recreate', layer: entry.layer }
  }

  // 取り消した操作をやり直す。戻り値の意味は undo() と同様。
  redo() {
    const entry = this.#redo.pop()
    if (!entry) return null
    if (entry.kind === 'add') {
      this.#commit([...this.#layers, entry.layer])
      this.#undo.push(entry)
      return { effect: 'recreate', layer: entry.layer }
    }
    this.#commit(this.#layers.filter((l) => l.layerId !== entry.layer.layerId))
    this.#undo.push(entry)
    return { effect: 'drop', layer: entry.layer }
  }
}
